import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import { toast } from "@/hooks/use-toast";
import { useCreateForm } from "@/features/forms/hooks/use-create-form";
import { getFormPreview } from "@/features/forms/services/get-form-preview";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { useRegionFilter } from "@/features/regions/hooks/use-region-filter";
import { ALL_REGIONS_CODE, getStoredRegion } from "@/features/regions/constants";
import { Region } from "@/features/regions/types/region";
import BundleWizardStepper, {
  WizardStep,
} from "@/features/bundles/ui/wizard/bundle-wizard-stepper";
import StepSource from "@/features/forms/ui/wizard/step-source";
import StepClassification from "@/features/forms/ui/wizard/step-classification";
import StepLinks from "@/features/forms/ui/wizard/step-links";
import StepReview from "@/features/forms/ui/wizard/step-review";
import {
  formWizardSchema,
  FormWizardData,
} from "@/features/forms/ui/wizard/wizard-form";
import {
  CreateFormData,
  FormPreview,
  FormProvider,
} from "@/pages/forms/types/form";
import { PROVIDER_LABEL } from "@/features/forms/constants";

const STEPS: WizardStep[] = [
  { key: "source", label: "Source" },
  { key: "classification", label: "Classification" },
  { key: "links", label: "Links" },
  { key: "review", label: "Review" },
];

export default function FormCreate() {
  const { region: regionSegment } = useParams();
  const prefillRegionCode = useRegionFilter();
  const { data: regions, isLoading } = useActiveRegions();

  const defaultRegion = useMemo(() => {
    if (!regions) return null;
    const stored = getStoredRegion();
    const code =
      prefillRegionCode ??
      (stored && stored !== ALL_REGIONS_CODE ? stored : undefined);
    return regions.find((r) => r.code === code) ?? null;
  }, [regions, prefillRegionCode]);

  if (isLoading || !regions) {
    return (
      <HasAccess permission="write">
        <div className="max-w-3xl mx-auto p-6 text-sm text-gray-500">
          Loading…
        </div>
      </HasAccess>
    );
  }

  return (
    <FormCreateWizard
      regionSegment={regionSegment}
      defaultRegion={defaultRegion}
    />
  );
}

function FormCreateWizard({
  regionSegment,
  defaultRegion,
}: {
  regionSegment?: string;
  defaultRegion: Region | null;
}) {
  const navigate = useNavigate();
  const createForm = useCreateForm();

  const [current, setCurrent] = useState(0);
  const [preview, setPreview] = useState<FormPreview | null>(null);
  const [fetching, setFetching] = useState(false);

  const form = useForm<FormWizardData>({
    resolver: zodResolver(formWizardSchema),
    defaultValues: {
      provider: "TYPEFORM",
      source_id: "",
      region_id: defaultRegion?.id ?? "",
      department: "",
      status: "ACTIVE",
      doc_link: "",
      bundle_ids: [],
    },
  });

  const goBackToList = () => navigate(`/${regionSegment ?? "all"}/forms`);

  const handleProviderChange = (p: FormProvider) => {
    form.setValue("provider", p);
    // A preview belongs to the provider it was fetched from; switching must not
    // carry a stale confirmation across and satisfy the step-0 gate.
    setPreview(null);
  };

  const handleFetch = async () => {
    const provider = form.getValues("provider");
    const sourceId = (form.getValues("source_id") || "").trim();
    if (!sourceId) {
      toast({
        title: "Source ID required",
        description: `Enter a ${PROVIDER_LABEL[provider]} source id to fetch.`,
        variant: "destructive",
      });
      return;
    }
    setFetching(true);
    try {
      const result = await getFormPreview(sourceId, provider);
      setPreview(result);
    } catch (error: any) {
      setPreview(null);
      toast({
        title: "Not found",
        description:
          error.response?.status === 404
            ? `No ${PROVIDER_LABEL[provider]} form for that source id.`
            : error.response?.data?.message || "Failed to fetch form.",
        variant: "destructive",
      });
    } finally {
      setFetching(false);
    }
  };

  const handleNext = async () => {
    const provider = form.getValues("provider");
    if (current === 0) {
      const ok = await form.trigger(["source_id"]);
      if (!ok) return;
      // Both providers can be fetched, so both must be: a form we cannot fetch
      // is a form whose webhooks we cannot map.
      if (!preview) {
        toast({
          title: "Fetch required",
          description: `Fetch the form from ${PROVIDER_LABEL[provider]} to continue.`,
          variant: "destructive",
        });
        return;
      }
    }
    if (current === 1) {
      const ok = await form.trigger(["region_id", "department", "status"]);
      if (!ok) return;
    }
    setCurrent((c) => Math.min(c + 1, STEPS.length - 1));
  };

  const onSubmit = async (data: FormWizardData) => {
    if (current !== STEPS.length - 1) return;
    try {
      const payload: CreateFormData = {
        provider: data.provider,
        source_id: data.source_id.trim(),
        department: data.department,
        status: data.status,
        region_id: data.region_id,
        doc_link: data.doc_link || undefined,
        bundle_ids: data.bundle_ids,
      };
      await createForm.mutateAsync(payload as any);
      goBackToList();
    } catch {
      // useCreateForm surfaces the error toast.
    }
  };

  const isLastStep = current === STEPS.length - 1;

  return (
    <HasAccess permission="write">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <button
          type="button"
          onClick={goBackToList}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" /> Forms / New Form
        </button>

        <BundleWizardStepper steps={STEPS} current={current} />

        <Form {...form}>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="space-y-6 rounded-lg border border-gray-200 p-6"
          >
            {current === 0 && (
              <StepSource
                form={form}
                preview={preview}
                fetching={fetching}
                onFetch={handleFetch}
                onProviderChange={handleProviderChange}
              />
            )}
            {current === 1 && <StepClassification form={form} />}
            {current === 2 && <StepLinks form={form} />}
            {current === 3 && <StepReview form={form} preview={preview} />}

            <div className="flex justify-between pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={
                  current === 0 ? goBackToList : () => setCurrent((c) => c - 1)
                }
              >
                {current === 0 ? "Cancel" : "← Back"}
              </Button>

              {isLastStep ? (
                <Button
                  type="button"
                  disabled={createForm.isPending}
                  onClick={form.handleSubmit(onSubmit)}
                >
                  {createForm.isPending ? "Creating…" : "✓ Create Form"}
                </Button>
              ) : (
                <Button type="button" onClick={handleNext}>
                  Next: {STEPS[current + 1].label} →
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </HasAccess>
  );
}
